import { useCallback, useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import type {
  DispatchOutcome,
  DispatchSessionBundle,
  LatLon,
  NearbyJob,
  NearbyOperative,
} from '@rapid-response/shared'
import { deriveOfficerKey } from '@rapid-response/shared'
import { getDispatchSession, getNearbyJobs, getNearestOperatives } from '~/server/dispatch'
import { OutcomeControls } from '~/components/dispatch/OutcomeControls'
import { DispatchMap } from '~/components/dispatch/DispatchMap'
import { PageHeader } from '~/components/PageHeader'
import { Badge } from '~/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '~/components/ui/collapsible'
import { Button } from '~/components/ui/button'
import { ChevronDown, ExternalLink, RefreshCw, Search } from 'lucide-react'
import {
  executionStatusClass,
  fmtDateTime,
  fmtDuration,
  fmtExecutionStatus,
  fmtMiles,
  jobViewUrl,
  lastSeenLabel,
  partnerContactsUrl,
} from '~/lib/dispatch-format'

const searchSchema = z.object({
  radius: z.coerce.number().int().min(1).max(30).catch(10),
  interval: z.coerce.number().int().min(1).catch(60),
})

export const Route = createFileRoute('/dispatch/$sessionId')({
  validateSearch: searchSchema,
  component: DispatchSession,
})

const JOBS_DEBOUNCE_MS = 400

function DispatchSession() {
  const { sessionId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()

  const [bundle, setBundle] = useState<DispatchSessionBundle | null>(null)
  const [assetLocation, setAssetLocation] = useState<LatLon | null>(null)
  const [operatives, setOperatives] = useState<NearbyOperative[]>([])
  const [jobs, setJobs] = useState<NearbyJob[]>([])
  const [opsLoading, setOpsLoading] = useState(false)
  const [jobsLoading, setJobsLoading] = useState(false)
  const [selectedOperativeId, setSelectedOperativeId] = useState<string | null>(null)
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null)
  const [radiusMiles, setRadiusMiles] = useState(search.radius)
  const [intervalMin, setIntervalMin] = useState(search.interval)
  const [outcomes, setOutcomes] = useState<Map<string, DispatchOutcome>>(() => new Map())
  const [refreshing, setRefreshing] = useState(false)

  const onRecorded = useCallback(
    (o: DispatchOutcome) =>
      setOutcomes((m) => new Map(m).set(`${o.targetType}:${o.targetKey}`, o)),
    [],
  )

  const onCleared = useCallback(
    (targetType: DispatchOutcome['targetType'], targetKey: string) =>
      setOutcomes((m) => {
        const next = new Map(m)
        next.delete(`${targetType}:${targetKey}`)
        return next
      }),
    [],
  )

  // Load session bundle client-side (no loader — auth token lives in-memory).
  useEffect(() => {
    let cancelled = false
    getDispatchSession({ data: { id: sessionId } })
      .then((b) => {
        if (cancelled) return
        setBundle(b)
        if (b.session.assetLat !== null && b.session.assetLon !== null) {
          setAssetLocation({ lat: b.session.assetLat, lon: b.session.assetLon })
        }
        const outcomeMap = new Map<string, DispatchOutcome>()
        for (const o of b.outcomes) {
          outcomeMap.set(`${o.targetType}:${o.targetKey}`, o)
        }
        setOutcomes(outcomeMap)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        toast.error(
          `Could not load session: ${e instanceof Error ? e.message : 'unknown error'}`,
        )
      })
    return () => {
      cancelled = true
    }
  }, [sessionId])

  // `jobNumber` drives the EGIS lookups; `assetNumber` is the resolved property
  // used for display + the map marker.
  const jobNumber = bundle?.session.jobNumber ?? null
  const assetNumber = bundle?.session.assetNumber ?? null

  const refresh = useCallback(async () => {
    if (jobNumber == null) return
    setRefreshing(true)
    try {
      const [opsRes, jobsRes] = await Promise.all([
        getNearestOperatives({ data: { jobNumber, sessionId, refresh: true } }),
        getNearbyJobs({ data: { jobNumber, radius: radiusMiles, interval: intervalMin, sessionId, refresh: true } }),
      ])
      setOperatives(opsRes.operatives)
      setJobs(jobsRes.jobs)
      const loc = opsRes.assetLocation ?? jobsRes.assetLocation
      if (loc) setAssetLocation(loc)
    } catch (e: unknown) {
      toast.error(`Refresh failed: ${e instanceof Error ? e.message : 'unknown error'}`)
    } finally {
      setRefreshing(false)
    }
  }, [jobNumber, sessionId, radiusMiles, intervalMin])

  // Operatives — refetch when the job is known (bundle loaded).
  useEffect(() => {
    if (jobNumber === null) return
    let cancelled = false
    setOpsLoading(true)
    getNearestOperatives({ data: { jobNumber, sessionId } })
      .then((res) => {
        if (cancelled) return
        setOperatives(res.operatives)
        if (res.assetLocation) setAssetLocation(res.assetLocation)
        setSelectedOperativeId(null)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setOperatives([])
        toast.error(`Operatives: ${e instanceof Error ? e.message : 'lookup failed'}`)
      })
      .finally(() => !cancelled && setOpsLoading(false))
    return () => {
      cancelled = true
    }
  }, [jobNumber, sessionId])

  // Jobs — refetch when job, radius or interval changes (debounced).
  useEffect(() => {
    if (jobNumber === null) return
    let cancelled = false
    setJobsLoading(true)
    const handle = setTimeout(() => {
      navigate({ search: { radius: radiusMiles, interval: intervalMin }, replace: true })
      getNearbyJobs({
        data: { jobNumber, radius: radiusMiles, interval: intervalMin, sessionId },
      })
        .then((res) => {
          if (cancelled) return
          setJobs(res.jobs)
          if (res.assetLocation) setAssetLocation(res.assetLocation)
          setSelectedJobId(null)
        })
        .catch((e: unknown) => {
          if (cancelled) return
          setJobs([])
          toast.error(`Jobs: ${e instanceof Error ? e.message : 'lookup failed'}`)
        })
        .finally(() => !cancelled && setJobsLoading(false))
    }, JOBS_DEBOUNCE_MS)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [jobNumber, radiusMiles, intervalMin, sessionId]) // eslint-disable-line react-hooks/exhaustive-deps

  if (bundle === null) {
    return (
      <div className="space-y-4">
        <PageHeader title="First Response" />
        <p className="text-sm text-muted-foreground">Loading session…</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-2">
        <PageHeader
          title="First Response"
          description={`Property #${bundle.session.assetNumber}${
            bundle.session.jobNumber ? ` · Job #${bundle.session.jobNumber}` : ''
          } · Session ${sessionId.slice(0, 8)}`}
        />
        <div className="mt-1 flex shrink-0 gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={refreshing || jobNumber == null}
            onClick={() => refresh()}
          >
            <RefreshCw className={`mr-1 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button size="sm" onClick={() => navigate({ to: '/' })}>
            <Search className="mr-1 h-4 w-4" />
            New search
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[380px_1fr]">
        <div className="space-y-4">
          <OfficersPanel
            operatives={operatives}
            loading={opsLoading}
            selectedId={selectedOperativeId}
            onSelect={setSelectedOperativeId}
            outcomes={outcomes}
            sessionId={sessionId}
            jobNumber={jobNumber}
            onRecorded={onRecorded}
            onCleared={onCleared}
          />
          <JobsPanel
            jobs={jobs}
            loading={jobsLoading}
            selectedId={selectedJobId}
            onSelect={setSelectedJobId}
            radiusMiles={radiusMiles}
            intervalMin={intervalMin}
            onRadius={setRadiusMiles}
            onInterval={setIntervalMin}
            outcomes={outcomes}
            sessionId={sessionId}
            onRecorded={onRecorded}
            onCleared={onCleared}
          />
          <Section title="Partners - Historical response" defaultOpen={false}>
            <p className="p-2 text-sm text-muted-foreground">Coming soon</p>
          </Section>
        </div>

        <div className="h-[70vh] overflow-hidden rounded-md border">
          <DispatchMap
            operatives={operatives}
            jobs={jobs}
            outcomes={outcomes}
            assetLocation={assetLocation}
            assetNumber={assetNumber}
            selectedOperativeId={selectedOperativeId}
            selectedJobId={selectedJobId}
            onSelectOperative={setSelectedOperativeId}
            onSelectJob={setSelectedJobId}
          />
        </div>
      </div>
    </div>
  )
}

function SearchArea({
  radiusMiles,
  intervalMin,
  onRadius,
  onInterval,
}: Readonly<{
  radiusMiles: number
  intervalMin: number
  onRadius: (n: number) => void
  onInterval: (n: number) => void
}>) {
  return (
    <div className="space-y-3 border-b p-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Search area
      </div>
      <label className="block space-y-1 text-xs text-muted-foreground">
        <span>
          Radius <span className="font-semibold text-foreground">{radiusMiles} mi</span>
        </span>
        <input
          type="range"
          min={1}
          max={30}
          step={1}
          value={radiusMiles}
          onChange={(e) => onRadius(Number(e.target.value))}
          className="w-full accent-[#FA5F00]"
        />
      </label>
      <label className="block space-y-1 text-xs text-muted-foreground">
        <span>
          Jobs within{' '}
          <span className="font-semibold text-foreground">{intervalMin} min</span>
        </span>
        <input
          type="range"
          min={15}
          max={240}
          step={15}
          value={intervalMin}
          onChange={(e) => onInterval(Number(e.target.value))}
          className="w-full accent-[#FA5F00]"
        />
      </label>
    </div>
  )
}

function Section({
  title,
  count,
  toolbar,
  defaultOpen = true,
  children,
}: Readonly<{
  title: string
  count?: number
  toolbar?: React.ReactNode
  defaultOpen?: boolean
  children: React.ReactNode
}>) {
  return (
    <Collapsible defaultOpen={defaultOpen} className="group/section rounded-md border">
      <CollapsibleTrigger className="flex w-full items-center justify-between border-b px-3 py-2 group-data-[state=closed]/section:border-b-0">
        <span className="flex items-center gap-2">
          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform group-data-[state=closed]/section:-rotate-90" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {title}
          </span>
        </span>
        {count !== undefined && <Badge variant="secondary">{count}</Badge>}
      </CollapsibleTrigger>
      <CollapsibleContent>
        {toolbar}
        <div className="max-h-[40vh] space-y-2 overflow-y-auto p-2">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  )
}

function OfficersPanel({
  operatives,
  loading,
  selectedId,
  onSelect,
  outcomes,
  sessionId,
  jobNumber,
  onRecorded,
  onCleared,
}: Readonly<{
  operatives: NearbyOperative[]
  loading: boolean
  selectedId: string | null
  onSelect: (id: string) => void
  outcomes: Map<string, DispatchOutcome>
  sessionId: string
  jobNumber: string | null
  onRecorded: (outcome: DispatchOutcome) => void
  onCleared: (targetType: DispatchOutcome['targetType'], targetKey: string) => void
}>) {
  return (
    <Section title="Officers" count={operatives.length}>
      {loading && <p className="p-2 text-sm text-muted-foreground">Locating officers…</p>}
      {!loading && operatives.length === 0 && (
        <p className="p-2 text-sm text-muted-foreground">No officers found.</p>
      )}
      {!loading &&
        operatives.map((op, i) => {
          const seen = lastSeenLabel(op.lastSeen)
          const active = op.id === selectedId
          const officerKey = deriveOfficerKey(op)
          return (
            <div
              key={op.id}
              className={`relative rounded-md border transition-colors hover:bg-accent ${
                active ? 'border-[#FA5F00] bg-[#FFF3E0]' : ''
              }`}
            >
              <button
                type="button"
                onClick={() => onSelect(op.id)}
                className="flex w-full items-start gap-3 p-2 pr-9 text-left"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#FA5F00] text-xs font-bold text-white">
                  {i + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold">
                    {op.firstName} {op.lastName}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {op.partnerName}
                    {op.phone ? ` · ${op.phone}` : ''}
                  </span>
                  <span className="mt-1 flex flex-wrap gap-1">
                    <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                      {fmtDuration(op.durationSeconds)}
                    </Badge>
                    <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                      {fmtMiles(op.distanceMiles)}
                    </Badge>
                    {seen && <Badge variant="outline">{seen}</Badge>}
                  </span>
                </span>
              </button>
              {op.partnerId && (
                <a
                  href={partnerContactsUrl(op.partnerId)}
                  target="_blank"
                  rel="noreferrer"
                  title="Open partner contacts in Chase2Base"
                  className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:bg-background hover:text-[#FA5F00]"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
              <div className="border-t px-2 pb-2 pt-1">
                <OutcomeControls
                  sessionId={sessionId}
                  targetType="officer"
                  targetKey={officerKey}
                  label={`${op.firstName} ${op.lastName} · ${op.partnerName}`}
                  acceptJobNumber={jobNumber}
                  current={outcomes.get(`officer:${officerKey}`)}
                  onRecorded={onRecorded}
                  onCleared={onCleared}
                />
              </div>
            </div>
          )
        })}
    </Section>
  )
}

function JobsPanel({
  jobs,
  loading,
  selectedId,
  onSelect,
  radiusMiles,
  intervalMin,
  onRadius,
  onInterval,
  outcomes,
  sessionId,
  onRecorded,
  onCleared,
}: Readonly<{
  jobs: NearbyJob[]
  loading: boolean
  selectedId: string | null
  onSelect: (id: string) => void
  radiusMiles: number
  intervalMin: number
  onRadius: (n: number) => void
  onInterval: (n: number) => void
  outcomes: Map<string, DispatchOutcome>
  sessionId: string
  onRecorded: (outcome: DispatchOutcome) => void
  onCleared: (targetType: DispatchOutcome['targetType'], targetKey: string) => void
}>) {
  return (
    <Section
      title="Jobs nearby"
      count={jobs.length}
      toolbar={
        <SearchArea
          radiusMiles={radiusMiles}
          intervalMin={intervalMin}
          onRadius={onRadius}
          onInterval={onInterval}
        />
      }
    >
      {loading && <p className="p-2 text-sm text-muted-foreground">Loading jobs…</p>}
      {!loading && jobs.length === 0 && (
        <p className="p-2 text-sm text-muted-foreground">No jobs in range.</p>
      )}
      {!loading &&
        jobs.map((job) => {
          const active = job.id === selectedId
          const locatable = job.lat !== null && job.lon !== null
          return (
            <div
              key={job.id}
              className={`relative rounded-md border transition-colors ${
                locatable ? 'hover:bg-accent' : 'opacity-70'
              } ${active ? 'border-[#FA5F00] bg-[#FFF3E0]' : ''}`}
            >
              <button
                type="button"
                disabled={!locatable}
                onClick={() => locatable && onSelect(job.id)}
                className={`block w-full p-2 pr-9 text-left ${
                  locatable ? '' : 'cursor-default'
                }`}
              >
              <span className="block truncate text-sm font-semibold">{job.service}</span>
              <span className="block truncate text-xs text-muted-foreground">
                {job.jobNumber} · {job.partnerName}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {job.allocatedTo ?? 'No officer allocated'}
              </span>
              {job.jobDate && (
                <span className="block text-xs text-muted-foreground">
                  Date: {fmtDateTime(job.jobDate)}
                </span>
              )}
              {job.jobDateEx && (
                <span className="block text-xs text-muted-foreground">
                  Last Job Date: {fmtDateTime(job.jobDateEx)}
                </span>
              )}
              <span className="mt-1 flex flex-wrap gap-1">
                {job.executionStatus && (
                  <Badge className={executionStatusClass(job.executionStatus)}>
                    {fmtExecutionStatus(job.executionStatus)}
                  </Badge>
                )}
                <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100">
                  {fmtMiles(job.distanceMiles)}
                </Badge>
              </span>
              </button>
              {job.jobId && (
                <a
                  href={jobViewUrl(job.jobId)}
                  target="_blank"
                  rel="noreferrer"
                  title="Open job in Chase2Base"
                  className="absolute right-2 top-2 rounded p-1 text-muted-foreground hover:bg-background hover:text-[#FA5F00]"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
              <div className="border-t px-2 pb-2 pt-1">
                <OutcomeControls
                  sessionId={sessionId}
                  targetType="job"
                  targetKey={job.jobNumber}
                  label={`${job.jobNumber} · ${job.service}`}
                  acceptJobNumber={null}
                  current={outcomes.get(`job:${job.jobNumber}`)}
                  onRecorded={onRecorded}
                  onCleared={onCleared}
                />
              </div>
            </div>
          )
        })}
    </Section>
  )
}
